<template>
  <div class="main">
    <CustomModal
      v-model="modalMixin.visible"
      :closable="modalMixin.closable"
      :mask-closable="modalMixin.maskClosable"
      :category="modalMixin.category"
      :title="modalMixin.title"
      :message="modalMixin.message"
      :action-buttons="modalMixin.actionButtons"
      :cancel-button="modalMixin.cancelButton"
      :is-form-valid="isFormValid"
      @closeModal="closeActionWindow"
    >
      <template #content>
        <!-- Dashboards Form -->
        <Form
          ref="dashboardsForm"
          :model="form"
          :rules="formRules"
          class="form"
        >
          <FormItem prop="title">
            <label>Title:</label>
            <Input
              v-model="form.title"
              name="dashboardTitle"
              placeholder="Dashboard Title"
              clearable
            />
          </FormItem>
          <FormItem prop="ownersArray">
            <label>Owners:</label>
            <Select
              v-model="form.ownersArray"
              placeholder="None"
              clearable
              filterable
              multiple
              :max-tag-count="2"
            >
              <Option
                v-for="user in users"
                :key="user.id"
                :label="user.email"
                :value="user.email"
              />
            </Select>
          </FormItem>
          <!-- TODO: allow create for category? -->
          <FormItem prop="categoryId">
            <label>Category:</label>
            <Select
              v-model="form.categoryId"
              placeholder="None"
              clearable
              filterable
            >
              <Option
                v-for="category in categories"
                :key="category.id"
                :label="category.name"
                :value="category.id"
              />
            </Select>
          </FormItem>
          <FormItem prop="tagsArray">
            <label>Tags:</label>
            <Select
              v-model="form.tagsArray"
              placeholder="None"
              clearable
              filterable
              multiple
              :max-tag-count="3"
              allow-create
              @on-create="addDashboardTag"
            >
              <Option
                v-for="tag in tags"
                :key="tag.key"
                :label="tag.value"
                :value="tag.value"
              />
            </Select>
          </FormItem>
          <FormItem prop="url">
            <label>URL:</label>
            <Input
              v-model="form.url"
              name="dashboardUrl"
              placeholder="Dashboard URL"
              clearable
            />
          </FormItem>
          <FormItem prop="description">
            <label>Description:</label>
            <Input
              v-model="form.description"
              name="dashboardDescription"
              placeholder="Dashboard Description"
              clearable
            />
          </FormItem>
          <div class="two-columns">
            <FormItem
              prop="type"
              class="column-left"
            >
              <label>Type:</label>
              <Select
                v-model="form.type"
                placeholder="None"
                clearable
              >
                <Option
                  v-for="type in types"
                  :key="type.value"
                  :label="type.label"
                  :value="type.value"
                />
              </Select>
            </FormItem>
            <FormItem
              prop="icon"
              class="column-right"
            >
              <label>Icon:</label>
              <Input
                v-model="form.icon"
                name="dashboardIcon"
                placeholder="Dashboard Icon"
                clearable
              />
            </FormItem>
          </div>
          <div class="two-columns">
            <FormItem class="column-left">
              <label>Rank:</label>
              <InputNumber
                v-model="form.rank"
                name="dashboardRank"
                placeholder="Rank"
                :min="0"
                :max="255"
                style="width: 100%;"
              />
            </FormItem>
            <FormItem class="column-right">
              <label>Status:</label>
              <Checkbox
                v-model="form.active"
                label="activeDashboard"
              >
                <span>Active Dashboard</span>
              </Checkbox>
            </FormItem>
          </div>
        </Form>
      </template>
    </CustomModal>

    <div class="title">
      <h2>{{ title }}</h2>
      <AdminToolBar
        show-add
        show-filter
        :is-filtered="isFiltered"
        @onAddItem="openAddDashboardForm"
        @onResetFilters="resetFilters"
      >
        <template #body>
          <!-- Dashboards Filter -->
          <div class="row">
            <div class="element-row">
              <p>By Keywords</p>
            </div>
          </div>
          <div class="row">
            <Input
              v-model="filters.keywords"
              placeholder="Filter by Keywords"
              clearable
              class="select-input"
            />
          </div>
        </template>
      </AdminToolBar>
    </div>
    <div
      ref="content"
      class="content"
    >
      <Spin
        v-if="loading"
        fix
      >
        <Icon
          type="ios-loading"
          size="18"
          class="spin"
        />
        <div>Loading</div>
      </Spin>
      <template v-else>
        <div
          v-if="!list.length"
          class="no-data"
        >
          There isn't any registered Dashboard.
        </div>
        <Table
          v-else
          :width="contentWidth"
          :max-height="contentHeight"
          :columns="columns"
          :data="filteredDashboards"
          no-data-text="No dashboards match the selected filter(s)"
          no-filtered-data-text="No dashboards match the selected filter(s)"
          class="table"
        >
          <template
            slot="owners"
            slot-scope="{ row }"
          >
            <p
              v-for="owner in row.owners.split(';')"
              :key="owner"
            >
              {{ owner }}
            </p>
          </template>
          <template
            slot="tags"
            slot-scope="{ row }"
          >
            <p
              v-for="tag in row.tags.split(';')"
              :key="tag"
            >
              {{ tag }}
            </p>
          </template>
          <template
            slot="status"
            slot-scope="{ row }"
          >
            <i
              :class="dashboardStatus(row.active)"
              @click="updateDashboard(row)"
            />
          </template>
          <template
            slot="actions"
            slot-scope="{ row }"
          >
            <i
              class="fas fa-edit action-icon"
              @click="openEditDashboardForm(row.id)"
            />
            <a
              :href="`/dashboards/${row.id}`"
              target="blank"
            >
              <i class="fas fa-link action-icon" />
            </a>
          </template>
        </Table>
      </template>
    </div>
  </div>
</template>

<script>
// TODO: Use vueX for handling the Dashboards list.
import CustomModal from '../CustomModal'
import AdminToolBar from './AdminToolBar'
import { customModalMixin } from '@/mixins/customModalMixin'
import UserMixin from '@/mixins/UserMixin'
import { SUCCESS, DEFAULT } from '@/constants/buttons'
import axios from 'axios'
import _ from 'lodash'

export default {
  components: {
    CustomModal,
    AdminToolBar
  },

  mixins: [
    customModalMixin,
    UserMixin
  ],

  props: {
    title: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      contentWidth: 0,
      contentHeight: 0,
      loading: true,
      list: [],
      columns: [
        {
          title: 'ID',
          key: 'id',
          sortable: true,
          width: '80'
        },
        {
          title: 'Title',
          key: 'title',
          sortable: true
        },
        {
          title: 'Owners',
          key: 'owners',
          slot: 'owners',
          width: '200',
          sortable: true,
          filterMultiple: false,
          filterMethod (value, row) {
            return row.owners.includes(value)
          }
        },
        {
          title: 'Tags',
          key: 'tags',
          slot: 'tags',
          width: '200',
          sortable: true,
          filterMethod (value, row) {
            return row.tags.indexOf(value) > -1
          }
        },
        {
          title: 'Type',
          key: 'type',
          width: '104',
          sortable: true,
          filterMultiple: false,
          filterMethod (value, row) {
            return row.type === value
          }
        },
        {
          title: 'Rank',
          key: 'rank',
          align: 'center',
          width: '80',
          sortable: true,
          sortType: 'desc'
        },
        {
          title: 'Status',
          slot: 'status',
          align: 'center',
          width: '104',
          sortable: true,
          filters: [
            {
              label: 'Active',
              value: 1
            },
            {
              label: 'Inactive',
              value: 0
            }
          ],
          filterMultiple: false,
          filterMethod (value, row) {
            return row.active === value
          }
        },
        {
          title: 'Actions',
          slot: 'actions',
          align: 'center',
          width: '104'
        }
      ],
      form: {
        title: '',
        ownersArray: [],
        owners: '',
        url: '',
        description: '',
        tagsArray: [],
        categoryId: null,
        tags: '',
        type: '',
        icon: '',
        rank: 0,
        status: 1
      },
      formRules: {
        title: [
          {
            type: 'string',
            required: true,
            message: 'Dashboard title cannot be empty',
            trigger: ['blur', 'change']
          }
        ],
        ownersArray: [
          {
            type: 'array',
            required: true,
            message: 'Owners cannot be empty',
            trigger: ['blur', 'change']
          }
        ],
        url: [
          {
            type: 'string',
            required: true,
            message: 'Dashboard URL cannot be empty',
            trigger: ['blur', 'change']
          }
        ],
        description: [
          {
            type: 'string',
            required: true,
            message: 'Description cannot be empty',
            trigger: ['blur', 'change']
          }
        ],
        tagsArray: [
          {
            type: 'array',
            required: true,
            message: 'Tags cannot be empty',
            trigger: ['blur', 'change']
          }
        ],
        type: [
          {
            type: 'string',
            required: true,
            message: 'Type cannot be empty',
            trigger: ['blur', 'change']
          }
        ],
        icon: [
          {
            type: 'string',
            required: true,
            message: 'Icon cannot be empty',
            trigger: ['blur', 'change']
          }
        ]
      },
      filters: {
        keywords: ''
      },
      tags: [],
      types: [],
      activeDashboard: {},
      fontAwesomeIcons: [],
      categories: []
    }
  },

  computed: {
    /**
     * Verifies is the dashboards form is valid (all required fields have been populated) or not.
     */
    isFormValid () {
      const { title, ownersArray, tagsArray, url, description: dashboardDescription, type, icon } = this.form
      return [title, ownersArray, tagsArray, url, dashboardDescription, type, icon].every(value => value && !_.isNil(value))
    },

    /**
     * Verifies if the dashboards list is using a filter from the toolbar.
     */
    isFiltered () {
      return !!this.filters.keywords
    },

    /**
     * Returns the filtered dashboards that meets with the filters specified from the toolbar.
     */
    filteredDashboards () {
      if (!this.isFiltered) {
        return this.list
      }

      const keywords = (this.filters.keywords || '').toLowerCase()
      return this.list.filter(dashboard => (
        dashboard.title.toLowerCase().match(keywords) ||
        ((dashboard.owners || '').toLowerCase().match(keywords)) ||
        ((dashboard.tags || '').toLowerCase().match(keywords)) ||
        ((dashboard.type || '').toLowerCase().match(keywords))
      ))
    }
  },

  mounted () {
    window.addEventListener('resize', this.getContentMeasures)

    if (!(this.users || []).length) {
      this.fetchAllUsers()
    }

    this.getContentMeasures()
    this.fetchDashboards()
    this.fetchDashboardCategories()
  },

  methods: {
    /**
     * Resets the filters of the Current Panel.
     * @param {String} panelName
     */
    resetFilters () {
      this.$set(this.filters, 'keywords', '')
    },

    /**
     * Gets the width and height of the content according with the browser measures.
     */
    getContentMeasures () {
      if (this.$refs.content) {
        this.contentWidth = this.$refs.content.offsetWidth
        this.contentHeight = this.$refs.content.offsetHeight
      }
    },

    async fetchDashboardCategories () {
      try {
        this.categories = (await axios.get(`${process.env.VUE_APP_API_URI}/dashboards/categories`)).data
      } catch (error) {
        console.error(`Fail to fetch dashboard categories: ${error}`)
      }
    },

    /**
     * Fetches the list of all the existing dashboards.
     */
    async fetchDashboards () {
      this.loading = true

      try {
        this.list = (await axios.get(`${process.env.VUE_APP_API_URI}/dashboards`)).data
        const ownersSet = {}
        const tagsSet = {}
        const typesSet = {}

        this.list.forEach(dashboard => {
          dashboard.tags = dashboard.categories || ''

          // Split the owners into an array to populate the set of all existing owners.
          const dashboardOwners = (dashboard.owners || '').split(';')
          dashboardOwners.forEach(owner => {
            if (!ownersSet[owner]) {
              ownersSet[owner] = true
            }
          })

          // Split the tags into an array to populate the set of all existing tags.
          const dashboardTags = (dashboard.tags || '').split(';')
          dashboardTags.forEach(tag => {
            if (!tagsSet[tag]) {
              tagsSet[tag] = true
            }
          })

          // Populate the set of all existing types of dashboards.
          const dashboardType = _.startCase(_.lowerCase(dashboard.type))
          if (!typesSet[dashboardType]) {
            typesSet[dashboardType] = dashboard.type
          }
        })

        this.tags = Object.keys(tagsSet).sort().map(tag => ({
          label: tag,
          value: tag
        }))

        this.types = Object.keys(typesSet).sort().map(type => ({
          label: type,
          value: typesSet[type]
        }))

        // Set the filter list for the Dashboards owners.
        const ownersColumn = this.columns.find(item => item.key === 'owners')
        ownersColumn.filters = Object.keys(ownersSet).sort().map(owner => ({
          label: owner,
          value: owner
        }))

        // Set the filter list for the Dashboards tags.
        const tagsColumn = this.columns.find(item => item.key === 'tags')
        tagsColumn.filters = this.tags

        // Set the filter list for the Dashboards types.
        const typeColumn = this.columns.find(item => item.key === 'type')
        typeColumn.filters = this.types
      } catch (error) {
        const content = `Failed to fetch Dashboards: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }
      this.loading = false
    },

    /**
     * Shows the proper icon according with the dashboard status: Active / Inactive
     * @param {Boolean} isActive
     * @return {String} - Icon class to be displayed.
     */
    dashboardStatus (isActive) {
      return isActive ? 'fas fa-check-circle action-icon active' : 'fas fa-ban action-icon inactive'
    },

    /**
     * Sets up the form by reseting it or populating it.
     * @param {Object} formData
     */
    setDashboardForm (formData = {}) {
      const {
        title = '',
        owners = '',
        tags = '',
        url = '',
        description = '',
        type = '',
        icon = '',
        rank = 0,
        active = true
      } = formData
      const categoryId = formData['category_id']

      // Set the form params.
      this.$set(this.form, 'title', title)
      this.$set(this.form, 'ownersArray', owners.split(';'))
      this.$set(this.form, 'categoryId', categoryId)
      this.$set(this.form, 'tagsArray', tags.split(';'))
      this.$set(this.form, 'url', url)
      this.$set(this.form, 'description', description)
      this.$set(this.form, 'type', type)
      this.$set(this.form, 'icon', icon)
      this.$set(this.form, 'rank', rank)
      this.$set(this.form, 'active', active === 1)

      if (_.isEmpty(formData)) {
        this.$refs['dashboardsForm'].resetFields()
        this.$set(this.form, 'active', true)
      }
    },

    /**
     * Opens a modal with a form to add a new Dashboard.
     */
    openAddDashboardForm () {
      this.activeDashboard = {}

      // Initialize the form params.
      this.setDashboardForm()

      // Set up the modal properties and display the modal.
      const modalData = {
        title: 'Add Dashboard',
        actionButtons: [
          {
            label: 'Save',
            type: SUCCESS,
            callback: this.saveDashboard.bind(this),
            restrictedByFormRules: true
          },
          {
            label: 'Cancel',
            type: DEFAULT,
            callback: this.cancelDashboardForm.bind(this)
          }
        ]
      }
      this.showModalMixin(modalData)
    },

    /**
     * Opens a modal with a form to edit an existin Dashboard.
     * @param {Number} dashboardId
     */
    openEditDashboardForm (dashboardId) {
      if (!dashboardId) {
        console.error('No dashboard id was provided to edit.')
        return
      }

      const activeDashboard = this.list.find(dashboard => dashboard.id === dashboardId)
      this.activeDashboard = activeDashboard

      // Set the form params.
      this.setDashboardForm(activeDashboard)

      // Set up the modal properties and display the modal.
      const modalData = {
        title: 'Edit Dashboard',
        actionButtons: [
          {
            label: 'Save',
            type: SUCCESS,
            callback: this.updateDashboard.bind(this),
            restrictedByFormRules: true
          },
          {
            label: 'Cancel',
            type: DEFAULT,
            callback: this.cancelDashboardForm.bind(this)
          }
        ]
      }
      this.showModalMixin(modalData)
    },

    /**
     * Adds a new tag into the dashboards tags list.
     * @param {String} newTag
     */
    addDashboardTag (newTag) {
      /**
       * Add the new tag with the property "isNew" which means that it can be deleted if the user cancels
       * the add/edition of the dashboard.
       */
      this.tags.push({
        label: newTag,
        value: newTag,
        isNew: true
      })

      // Sort the tags list.
      this.tags.sort((a, b) => a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1)
    },

    /**
     * Changes the status of the new tags as "false" and merge them and the owners into a String list
     * (separated by semicolon) in order to be stored into DataBase.
     */
    prepareDashboardTagsAndOwners () {
      this.tags.forEach(tag => {
        tag.isNew = false
      })

      this.form.owners = this.form.ownersArray.join(';')
      this.form.tags = this.form.tagsArray.join(';')
    },

    /**
     * Saves a new Dashboard or updates an existing one if the user edits a Dashboard.
     */
    async saveDashboard () {
      this.prepareDashboardTagsAndOwners()
      const dashboard = this.form

      const {
        title,
        description,
        owners,
        url,
        categoryId,
        tags,
        type,
        icon,
        rank,
        active
      } = dashboard

      try {
        await axios.post(`${process.env.VUE_APP_API_URI}/dashboards`, {
          title,
          description,
          owners,
          url,
          categoryId,
          tags,
          type,
          icon,
          rank,
          active
        })

        this.$Message.success({
          content: `Dashboard ${title} has been added`,
          duration: 5
        })

        this.closeActionWindow()
        this.fetchDashboards()
      } catch (error) {
        const content = `Failed to save new Dashboard: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }
    },

    /**
     * Updates a specific Dashboard.
     * If "toggleStatus" is set as TRUE, then it will update only the status (Active / Inactive).
     * Otherwise, it will update the modified fields from the Dashboard.
     * @param {Boolean} toggleStatus
     */
    async updateDashboard (dashboardData = null) {
      this.prepareDashboardTagsAndOwners()
      const dashboard = dashboardData || this.form

      if (dashboardData) {
        dashboard.active = !dashboard.active
      }

      const dashboardId = (dashboardData || this.activeDashboard).id
      const {
        title,
        description,
        owners,
        url,
        categoryId,
        tags,
        type,
        icon,
        rank,
        active
      } = dashboard

      try {
        await axios.put(`${process.env.VUE_APP_API_URI}/dashboards/${dashboardId}`, {
          title,
          description,
          owners,
          url,
          categoryId,
          tags,
          type,
          icon,
          rank,
          active
        })

        /**
         * If the dashboard was updated only by the status, then display a message with the current status
         * of the dashboard. Otherwise, just display that the dashboard has been updated.
         */
        const dashboardStatus = active ? 'Active' : 'Inactive'
        const content = dashboardData
          ? `<b>${title}</b> dashboard has been changed to ${dashboardStatus}`
          : `<b>${title}</b> dashboard has been updated`

        this.$Message.success({
          content,
          duration: 5
        })

        this.closeActionWindow()
        this.fetchDashboards()
      } catch (error) {
        const content = `Failed to update Dashboard ${title}: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }
    },

    /**
     * Cancels the addition of a new dashboard or the edition of an existing one.
     */
    cancelDashboardForm () {
      // Remove any new Tag added within the form.
      this.tags = this.tags.filter(tag => !tag.isNew)
      this.closeActionWindow()
    },

    /**
     * Closes the action window (modal).
     */
    closeActionWindow () {
      this.hideModalMixin()
    }
  }
}
</script>

<style scoped>
  .main {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .spin,
  .table >>> .ivu-table-cell .spin {
    animation: ani-demo-spin 1s linear infinite;
  }

  .row {
    margin: 0;
    padding: 4px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
  }

  .row .element-row,
  .row .element-row p {
    margin: 0;
    padding: 0 8px;
    width: 100%;
  }

  .row .element-row {
    padding: 0;
    display: flex;
    flex-direction: row;
  }

  .row .element-row p {
    margin: 0;
  }

  .row .select-input {
    width: 100%;
  }

  .form >>> .ivu-form-item-content {
    display: flex;
    flex-direction: column;
  }

  .two-columns {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .two-columns .column-left {
    margin-left: 0;
    margin-right: 4px;
    width: calc(50% - 8px)
  }

  .two-columns .column-right {
    margin-left: 4px;
    margin-right: 0;
    width: calc(50% - 4px)
  }

  .title {
    margin-bottom: 8px;
    padding-bottom: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 1px solid var(--color-gray-border);
  }

  .title h2 {
    font-size: 16px;
    width: 100%;
  }

  .title .actions {
    width: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .content {
    width: 100%;
    height: calc(100% - 38px);
    overflow: auto;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .no-data {
    font-size: 24px;
    color: var(--color-gray);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .action-icon {
    width: 24px;
    text-align: center;
    font-size: 16px;
    color: var(--color-black);
    cursor: pointer;
  }

  .table >>> .ivu-table-cell {
    padding: 0 8px;
  }

  .table >>> .ivu-table-cell .fas {
    font-size: 16px;
  }

  .table >>> .ivu-table-cell .action-icon {
    cursor: pointer;
  }

  .table >>> .ivu-table-cell .action-icon:not(:first-child) {
    margin-left: 8px;
  }

  .table >>> .ivu-table-cell .action-icon.hidden {
    display: none;
  }

  .table >>> .ivu-table-cell .fas.fa-exclamation-circle {
    color: var(--color-yellow-dark);
  }

  .table >>> .ivu-table-cell .fas.fa-check-circle {
    color: var(--color-green-lime);
  }

  .table >>> .ivu-table-cell .fas.fa-ban {
    color: var(--color-red);
  }
</style>
